import * as _ from 'lodash';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';

import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { IImageListDto, imageListDto } from 'src/assets/common/models/image-list-dto.g';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';

export const imageListData = {
    guid: 'guid',
    nr: 'nr',
    fileVariantType: 'fileVariantType',
    contentType: 'contentType',
    folderShardPath: 'folderShardPath',
    description: 'description',
    fileName: 'fileName',
    path: 'path',
    extension: 'extension',
    url: 'url',
    thumbUrl: 'thumbUrl',
    originGuid: 'originGuid',
    thumbGuid: 'thumbGuid',
    height: 'height',
    width: 'width',
    deleted: 'deleted',
    inactive: 'inactive',
    shared: 'shared',
    isMain: 'isMain',
    isFallback: 'isFallback',
    datum: 'datum',
    maandOmschrijving: 'maandOmschrijving',
    jaartal: 'jaartal',
    dateShort: 'dateShort',
    version: 'version',
} as const;

export type TImageListData = (typeof imageListData)[keyof typeof imageListData];

export interface IImageListData extends ILevelGeneralListData {
    [imageListData.guid]: string;
    [imageListData.nr]: number | null;
    [imageListData.fileVariantType]: string | null;
    [imageListData.contentType]: string | null;
    [imageListData.folderShardPath]: string | null;
    [imageListData.description]: string | null;
    [imageListData.fileName]: string | null;
    [imageListData.path]: string | null;
    [imageListData.extension]: string | null;
    [imageListData.url]: string | null;
    [imageListData.thumbUrl]: string | null;
    [imageListData.originGuid]: string | null;
    [imageListData.thumbGuid]: string | null;
    [imageListData.height]: number | null;
    [imageListData.width]: number | null;
    [imageListData.deleted]: boolean;
    [imageListData.inactive]: boolean;
    [imageListData.shared]: boolean;
    [imageListData.isMain]: boolean;
    [imageListData.isFallback]: boolean;
    [imageListData.datum]: number | null;
    [imageListData.maandOmschrijving]: string | null;
    [imageListData.jaartal]: number | null;
    [imageListData.dateShort]: string | null;
    [imageListData.version]: string | null;
}

export class ImageListData
    implements IImageListData, ILevelGeneralListData
{
    [imageListData.guid]!: string;
    [imageListData.nr]: number | null = null;
    [imageListData.fileVariantType]!: string;
    [imageListData.contentType]!: string;
    [imageListData.folderShardPath]: string | null = null;
    [imageListData.description]: string | null = null;
    [imageListData.fileName]: string | null = null;
    [imageListData.path]: string | null = null;
    [imageListData.extension]: string | null = null;
    [imageListData.url]: string | null = null;
    [imageListData.thumbUrl]: string | null = null;
    [imageListData.originGuid]: string | null = null;
    [imageListData.thumbGuid]: string | null = null;
    [imageListData.height]: number | null = null;
    [imageListData.width]: number | null = null;
    [imageListData.deleted]!: boolean;
    [imageListData.inactive]!: boolean;
    [imageListData.shared]: boolean = false;
    [imageListData.isMain]: boolean = false;
    [imageListData.isFallback]: boolean = false;
    [imageListData.datum]: number | null = null;
    [imageListData.maandOmschrijving]: string | null = null;
    [imageListData.jaartal]: number | null = null;
    [imageListData.dateShort]: string | null = null;
    [imageListData.version]: string | null = null;

    private constructor(modelListDto?: IImageListDto | null) {
        if (modelListDto == null || isEmptyObject(modelListDto)) return;

        const __box = modelListDto[imageListDto.fileBoxDto];
        
        this[imageListData.guid] = modelListDto[imageListDto.guid]!;
        this[imageListData.nr] = modelListDto[imageListDto.nr];
        this[imageListData.fileVariantType] = modelListDto[imageListDto.fileVariantType]!;
        this[imageListData.contentType] = modelListDto[imageListDto.contentType]!;
        this[imageListData.fileName] = modelListDto[imageListDto.fileName];
        this[imageListData.extension] = modelListDto[imageListDto.extension];
        this[imageListData.description] = modelListDto[imageListDto.description];
        this[imageListData.folderShardPath] = modelListDto[imageListDto.folderShardPath];
        this[imageListData.originGuid] = __box?.[imageBoxDto.photoDto]?.[imageDto.guid] ?? null;
        this[imageListData.thumbGuid] = __box?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? this[imageListData.originGuid];
        this[imageListData.version] =
            __box?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn]
            ?? __box?.[imageBoxDto.photoDto]?.[imageDto.modifiedOn]
            ?? null;
        this[imageListData.height] = modelListDto[imageListDto.height];
        this[imageListData.width] = modelListDto[imageListDto.width];
        this[imageListData.inactive] = modelListDto[imageListDto.inactive]!;
        this[imageListData.deleted] = modelListDto[imageListDto.deleted]!;
        this[imageListData.shared] = modelListDto[imageListDto.shared] ?? false;
        this[imageListData.isMain] = modelListDto[imageListDto.isMain] ?? false;
        this[imageListData.isFallback] = modelListDto[imageListDto.isFallback] ?? false;
        const __createdOn = modelListDto[imageListDto.createdOn];
        if (__createdOn) {
            const __d = new Date(__createdOn);
            this[imageListData.datum] = __d.getDate();
            this[imageListData.maandOmschrijving] = __d.toLocaleDateString(undefined, { month: 'long' });
            this[imageListData.jaartal] = __d.getFullYear();
            // Korte notatie d-M-yy (zonder leading zeros) voor compacte weergave naast een omschrijving.
            this[imageListData.dateShort] = `${__d.getDate()}-${__d.getMonth() + 1}-${String(__d.getFullYear()).slice(-2)}`;
        }
    }

    static create(modelListDto?: IImageListDto | null): IImageListData | null {
        return modelListDto ? new ImageListData(modelListDto) : null;
    }
}
